import { useMemo, useState } from "react";
import {
  ArrowRight,
  CalendarDays,
  Check,
  CheckCircle2,
  Circle,
  Clock3,
  Inbox,
  Plus,
  Search,
  UserRound
} from "lucide-react";
import clsx from "clsx";
import type { Task, TaskStatus } from "../types";
import { saveRecord, softDeleteRecord } from "../lib/db";
import {
  addLocalDays,
  formatShortDate,
  isFollowUpDue,
  isTaskDue,
  localDateKey,
  nowIso
} from "../lib/time";
import {
  Button,
  ConfirmDialog,
  EmptyState,
  PageHeader,
  Segmented,
  StatusPill
} from "../components/ui";
import { TaskEditor } from "../components/TaskEditor";

type TaskFilter = "all" | TaskStatus;

export function TasksView({
  tasks,
  notify
}: {
  tasks: Task[];
  notify: (message: string, tone?: "default" | "error") => void;
}) {
  const [filter, setFilter] = useState<TaskFilter>("all");
  const [area, setArea] = useState("all");
  const [dueFilter, setDueFilter] = useState("all");
  const [query, setQuery] = useState("");
  const [editorTask, setEditorTask] = useState<Task | null | undefined>(undefined);
  const [deleteTask, setDeleteTask] = useState<Task | undefined>();

  const activeTasks = tasks.filter((task) => task.status !== "done" && task.status !== "dropped");
  const areas = [...new Set(tasks.map((task) => task.area).filter(Boolean))].sort();
  const statusCounts = {
    all: tasks.length,
    inbox: tasks.filter((task) => task.status === "inbox").length,
    next: tasks.filter((task) => task.status === "next").length,
    doing: tasks.filter((task) => task.status === "doing").length,
    waiting: tasks.filter((task) => task.status === "waiting").length,
    done: tasks.filter((task) => task.status === "done").length,
    dropped: tasks.filter((task) => task.status === "dropped").length
  };

  const filtered = useMemo(() => {
    const statusOrder: Record<TaskStatus, number> = {
      doing: 0,
      next: 1,
      waiting: 2,
      inbox: 3,
      done: 4,
      dropped: 5
    };
    return tasks
      .filter((task) => filter === "all" || task.status === filter)
      .filter((task) => area === "all" || task.area === area)
      .filter((task) => {
        if (dueFilter === "all") return true;
        if (dueFilter === "none") return !task.dueAt;
        if (dueFilter === "overdue") {
          return !["done", "dropped"].includes(task.status) && isTaskDue(task.dueAt);
        }
        const today = localDateKey();
        if (dueFilter === "today") return task.dueAt === today;
        if (dueFilter === "week") {
          return Boolean(
            task.dueAt &&
              task.dueAt >= today &&
              task.dueAt <= addLocalDays(today, 7)
          );
        }
        return true;
      })
      .filter((task) =>
        `${task.title} ${task.outcome} ${task.nextAction} ${task.waitingOn ?? ""} ${task.body}`
          .toLowerCase()
          .includes(query.toLowerCase())
      )
      .sort(
        (a, b) =>
          statusOrder[a.status] - statusOrder[b.status] ||
          (a.dueAt ?? "9999").localeCompare(b.dueAt ?? "9999") ||
          b.updatedAt.localeCompare(a.updatedAt)
      );
  }, [area, dueFilter, filter, query, tasks]);

  async function complete(task: Task) {
    await saveRecord({
      ...task,
      status: "done",
      completedAt: nowIso()
    });
    notify("任务已完成");
  }

  return (
    <div className="view">
      <PageHeader
        eyebrow="任务池"
        title="让每件事都有结果和下一步"
        description="收件时只写标题；真正准备推进时，再补清预期结果与下一步。"
        action={
          <Button type="button" variant="primary" onClick={() => setEditorTask(null)}>
            <Plus size={17} /> 新建待办
          </Button>
        }
      />

      <div className="summary-strip">
        <div>
          <span>正在推进</span>
          <strong>{activeTasks.filter((task) => ["next", "doing"].includes(task.status)).length}</strong>
        </div>
        <div>
          <span>等待他人</span>
          <strong>{statusCounts.waiting}</strong>
        </div>
        <div>
          <span>待归位</span>
          <strong>{statusCounts.inbox}</strong>
        </div>
        <div className={clsx(tasks.some((task) => isTaskDue(task.dueAt) && !["done", "dropped"].includes(task.status)) && "attention")}>
          <span>已逾期</span>
          <strong>
            {tasks.filter((task) => !["done", "dropped"].includes(task.status) && isTaskDue(task.dueAt)).length}
          </strong>
        </div>
      </div>

      <section className="surface task-workspace">
        <div className="task-toolbar">
          <Segmented
            value={filter}
            onChange={setFilter}
            ariaLabel="按状态筛选"
            options={[
              { value: "all", label: "全部", count: statusCounts.all },
              { value: "inbox", label: "收件箱", count: statusCounts.inbox },
              { value: "next", label: "下一步", count: statusCounts.next },
              { value: "doing", label: "进行中", count: statusCounts.doing },
              { value: "waiting", label: "等待", count: statusCounts.waiting },
              { value: "done", label: "完成", count: statusCounts.done }
            ]}
          />
          <div className="filter-row">
            <label className="search-field">
              <Search size={17} />
              <input value={query} onChange={(event) => setQuery(event.target.value)} placeholder="搜索任务" />
            </label>
            <select className="input compact-select" value={area} onChange={(event) => setArea(event.target.value)}>
              <option value="all">全部领域</option>
              {areas.map((item) => (
                <option key={item} value={item}>{item}</option>
              ))}
            </select>
            <select className="input compact-select" value={dueFilter} onChange={(event) => setDueFilter(event.target.value)}>
              <option value="all">全部日期</option>
              <option value="overdue">已逾期</option>
              <option value="today">今天到期</option>
              <option value="week">未来七天</option>
              <option value="none">未设日期</option>
            </select>
          </div>
        </div>

        {filtered.length === 0 ? (
          <EmptyState
            icon={<Inbox size={26} />}
            title="这里暂时没有任务"
            description={query || area !== "all" ? "换个筛选条件再找找。" : "把第一件要推进的事记下来。"}
            action={
              !query && area === "all" ? (
                <Button type="button" variant="primary" onClick={() => setEditorTask(null)}>
                  <Plus size={16} /> 新建待办
                </Button>
              ) : undefined
            }
          />
        ) : (
          <div className="task-list">
            {filtered.map((task) => {
              const overdue = !["done", "dropped"].includes(task.status) && isTaskDue(task.dueAt);
              const followUpDue = task.status === "waiting" && isFollowUpDue(task.followUpAt);
              const needsClarification = task.status !== "inbox" && (!task.outcome || !task.nextAction);
              return (
                <article key={task.id} className={clsx("task-row", overdue && "overdue", task.status === "done" && "completed")}>
                  <button
                    type="button"
                    className="task-check"
                    aria-label={task.status === "done" ? "标记为下一步" : "标记完成"}
                    onClick={() =>
                      void saveRecord({
                        ...task,
                        status: task.status === "done" ? "next" : "done",
                        completedAt: task.status === "done" ? undefined : nowIso()
                      })
                    }
                  >
                    {task.status === "done" ? <CheckCircle2 size={21} /> : <Circle size={21} />}
                  </button>
                  <button type="button" className="task-main" onClick={() => setEditorTask(task)}>
                    <span className="task-title-line">
                      <strong>{task.title}</strong>
                      <StatusPill status={task.status} compact />
                      {task.area && <span className="area-label">{task.area}</span>}
                    </span>
                    <span className="task-detail-line">
                      {task.outcome ? (
                        <span>{task.outcome}</span>
                      ) : (
                        <span className="muted">尚未写清预期结果</span>
                      )}
                    </span>
                    <span className="task-meta-line">
                      {task.nextAction && <span><ArrowRight size={14} /> {task.nextAction}</span>}
                      {task.dueAt && (
                        <span className={overdue ? "danger-text" : ""}>
                          <CalendarDays size={14} /> {formatShortDate(task.dueAt)}
                        </span>
                      )}
                      {task.status === "waiting" && (
                        <span className={followUpDue ? "danger-text" : "amber-text"}>
                          <UserRound size={14} /> {task.waitingOn || "未填写"} · {formatShortDate(task.followUpAt)}
                        </span>
                      )}
                      {needsClarification && (
                        <span className="attention-text"><Clock3 size={14} /> 需要补清承诺</span>
                      )}
                    </span>
                  </button>
                  {task.status !== "done" && task.status !== "dropped" && (
                    <button type="button" className="quick-complete" onClick={() => void complete(task)}>
                      <Check size={16} /> 完成
                    </button>
                  )}
                </article>
              );
            })}
          </div>
        )}
      </section>

      {editorTask !== undefined && (
        <TaskEditor
          task={editorTask ?? undefined}
          onClose={() => setEditorTask(undefined)}
          onSaved={() => notify("任务已保存")}
          onDelete={editorTask ? setDeleteTask : undefined}
        />
      )}

      {deleteTask && (
        <ConfirmDialog
          title="删除这项待办？"
          description={`“${deleteTask.title}”会被移入已删除记录，不再出现在列表中。`}
          confirmLabel="删除"
          onClose={() => setDeleteTask(undefined)}
          onConfirm={() => {
            void softDeleteRecord(deleteTask.id).then(() => {
              setEditorTask(undefined);
              setDeleteTask(undefined);
              notify("任务已删除");
            });
          }}
        />
      )}
    </div>
  );
}
